/**
 * Tauri 환경 감지 헬퍼
 * - Tauri v2는 window.__TAURI_INTERNALS__를 주입
 */
export function isTauri(): boolean {
  return typeof window !== 'undefined' && !!(window as any).__TAURI_INTERNALS__;
}

/** 우클릭 시 WebView2 편집 메뉴(잘라내기/복사/붙여넣기)가 쓸모 있는 input type */
const TEXT_INPUT_TYPES = new Set(['', 'text', 'search', 'email', 'url', 'tel', 'password', 'number']);

function isTextEditable(el: Element): boolean {
  const field = el.closest('input, textarea, [contenteditable]');
  if (!field) return false;
  if (field instanceof HTMLTextAreaElement) return true;
  if (field instanceof HTMLInputElement) return TEXT_INPUT_TYPES.has(field.type);
  return (field as HTMLElement).isContentEditable;
}

let contextMenuGuardInstalled = false;

/**
 * 데스크톱 앱에서 빈 곳을 우클릭할 때 뜨는 WebView2 페이지 메뉴(뒤로/새로 고침/다른 이름으로
 * 저장/인쇄)를 막는다. '새로 고침'은 앱을 통째로 다시 로드한다.
 *
 * 쓸모 있는 기본 메뉴는 남긴다 — 입력창(붙여넣기), 선택한 글자(복사), 링크·이미지.
 * 앱 자체 메뉴(대화방 행·메시지 우클릭)는 각 핸들러가 먼저 preventDefault 하므로 영향이 없다.
 * 웹·PWA 브라우저의 우클릭 메뉴는 건드리지 않는다.
 *
 * ⚠ 버블 단계로 등록한다(capture 금지). `ChannelListItem`의 onContextMenu는 useLongPress 처리
 * 뒤의 `e.defaultPrevented`로 터치/마우스를 가르는데, capture에서 먼저 막으면 그 판정이 뒤집혀
 * 대화방 우클릭 메뉴가 열리지 않는다. React 18은 root 컨테이너에서 위임하므로 window 버블
 * 리스너는 모든 React 핸들러 뒤에 돈다.
 */
export function installDesktopContextMenuGuard(): void {
  if (!isTauri() || contextMenuGuardInstalled) return;
  contextMenuGuardInstalled = true;
  window.addEventListener('contextmenu', (e) => {
    if (e.defaultPrevented) return;
    const target = e.target instanceof Element ? e.target : null;
    if (target) {
      if (isTextEditable(target)) return;
      if (target.closest('a[href], img, video')) return;
    }
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) return;
    e.preventDefault();
  });
}

/**
 * 업데이트 설치 후 앱 재시작.
 *
 * ⚠ `relaunch()`를 직접 부르지 말 것. `relaunch()`는 새 프로세스를 띄운 **뒤** 현재
 * 프로세스를 종료하므로, 중복 실행 잠금(tauri-plugin-single-instance)을 먼저 풀지 않으면
 * 새 프로세스가 아직 살아 있는 기존 인스턴스를 감지해 창만 focus 하고 스스로 종료한다
 * → 곧이어 기존 프로세스도 종료되어 **앱이 사라진다**.
 *
 * 자동 업데이트는 전 데스크톱 사용자에게 닿는 유일한 경로라 여기서 실패하면 되돌릴 수 없다.
 * 재시작 경로가 늘어나도 이 함수만 쓰면 순서를 빠뜨릴 수 없다.
 */
export async function relaunchApp(): Promise<void> {
  // 잠금 해제 실패가 재시작 자체를 막지 않게 한다(fail-open).
  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('release_single_instance_lock');
  } catch (err) {
    console.error('Failed to release single-instance lock before relaunch:', err);
  }
  const { relaunch } = await import('@tauri-apps/plugin-process');
  await relaunch();
}

/**
 * 작업표시줄 빨간 점멸 시작/정지.
 *
 * Rust `flash_taskbar`는 `FlashWindowEx(FLASHW_TRAY | FLASHW_TIMER)`로 무한 점멸을 걸고,
 * 트레이에 숨겨진 상태라면 `SW_SHOWMINNOACTIVE`로 포커스를 뺏지 않고 작업표시줄 버튼만 만든다.
 * 이때 그 창은 렌더된 적이 없어 DWM에 썸네일이 없으므로, 같은 함수가 오버 시 보일 카드를
 * 직접 그리는 모드(iconic representation)를 함께 켜고 `stop: true`에서 끈다.
 * 호출부마다 동적 import를 복붙하면 인자 이름(`stop`)이 갈라지므로 여기 하나로 모은다.
 */
export async function flashTaskbar(stop: boolean): Promise<void> {
  if (!isTauri()) return;
  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('flash_taskbar', { stop });
  } catch {
    // 점멸 실패가 앱 동작을 막을 이유가 없다
  }
}

/* ------------------------------------------------------------------ *
 * 작업표시줄 오버레이 배지 (Windows, ITaskbarList3::SetOverlayIcon)
 * ------------------------------------------------------------------ */

/** 배지 캔버스 한 변(px). Windows가 16/20/24/32로 축소하므로 최대 크기로 그린다. */
const BADGE_PX = 32;

/** 마지막으로 요청된 값 (아직 적용 전일 수 있다) */
let requestedBadgeCount = 0;
/** 실제로 작업표시줄에 반영이 끝난 값. null이면 미상 → 다음 요청에서 무조건 다시 그린다. */
let appliedBadgeCount: number | null = null;
/** 적용 루프 중복 실행 방지 */
let badgeApplying = false;

/**
 * 안읽음 숫자 배지를 캔버스로 그려 RGBA(row-major, 좌상단부터)로 돌려준다.
 *
 * ⚠ 흰 테두리를 반드시 그린다 — 앱 아이콘 자체가 빨간 말풍선이라(`src-tauri/icons/icon.png`)
 * 테두리가 없으면 빨간 배지가 아이콘에 묻혀 숫자가 보이지 않는다.
 */
function drawBadgeRgba(count: number): Uint8Array | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = BADGE_PX;
  canvas.height = BADGE_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // 32px 안에 들어가는 글자는 3자가 한계다
  const label = count > 99 ? '99+' : String(count);
  const center = BADGE_PX / 2;

  ctx.clearRect(0, 0, BADGE_PX, BADGE_PX);
  ctx.beginPath();
  ctx.arc(center, center, 14.5, 0, Math.PI * 2);
  ctx.fillStyle = '#E50012'; // brand-600 (로고 원색)
  ctx.fill();
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = '#FFFFFF';
  ctx.stroke();

  const fontSize = label.length >= 3 ? 13 : label.length === 2 ? 17 : 20;
  // 캔버스는 폰트 로딩을 기다리지 않는다 — 숫자는 폴백 폰트로도 문제없다
  ctx.font = `bold ${fontSize}px "Pretendard Variable", system-ui, sans-serif`;
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, center, center + 1);

  return new Uint8Array(ctx.getImageData(0, 0, BADGE_PX, BADGE_PX).data);
}

async function applyBadgeCount(count: number): Promise<void> {
  const { getCurrentWindow } = await import('@tauri-apps/api/window');
  const win = getCurrentWindow();
  const rgba = count > 0 ? drawBadgeRgba(count) : null;
  if (!rgba) {
    // 인자 없이 부르면 오버레이 해제
    await win.setOverlayIcon();
    return;
  }
  const { Image } = await import('@tauri-apps/api/image');
  const image = await Image.new(rgba, BADGE_PX, BADGE_PX);
  try {
    await win.setOverlayIcon(image);
  } finally {
    // Image는 Rust 리소스 테이블 엔트리다. 닫지 않으면 갱신할 때마다 누수된다.
    // (set_overlay_icon은 dispatch 전에 픽셀을 복제하므로 바로 닫아도 안전하다)
    await image.close().catch(() => {});
  }
}

/** 요청값과 반영값이 같아질 때까지 따라간다 — 안읽음이 연달아 바뀌어도 마지막 값 하나만 남는다. */
async function drainBadgeQueue(): Promise<void> {
  if (badgeApplying) return;
  badgeApplying = true;
  try {
    while (appliedBadgeCount !== requestedBadgeCount) {
      const target = requestedBadgeCount;
      await applyBadgeCount(target);
      appliedBadgeCount = target;
    }
  } catch {
    // 실패하면 반영값을 미상으로 되돌려 다음 요청 때 다시 그리게 한다.
    // 배지를 못 칠한 것이 앱 동작을 막을 이유가 없다(clear_notifications와 같은 규칙).
    appliedBadgeCount = null;
  } finally {
    badgeApplying = false;
  }
}

/**
 * 작업표시줄 아이콘 우하단의 안읽음 숫자 배지를 갱신한다 (0이면 해제).
 *
 * 숫자는 모바일 PWA 앱 아이콘 배지·하단 탭바와 **같은 소스(totalUnread)** 를 쓴다 —
 * 호출부는 `App.tsx`의 `setAppBadgeCount()` 하나뿐이고, 그 함수는 파생값 effect에만
 * 반응한다. `setUnreadCounts` 호출부가 15곳이라 개별 경로에 심으면 반드시 누락된다.
 *
 * ⚠ Windows 10의 "작은 작업 표시줄 단추" 설정에서는 OS가 오버레이 아이콘을 표시하지 않는다
 * (ITaskbarList3의 제약). 그 경우에도 빨간 점멸은 그대로 동작한다.
 *
 * 트레이 숨김 중 오버 시 뜨는 썸네일 카드의 문구("새 메시지 N건")도 같은 숫자를 쓴다 —
 * 별도 깔때기를 만들면 배지 숫자와 카드 숫자가 갈라진다.
 */
export async function setTaskbarBadge(count: number): Promise<void> {
  if (!isTauri()) return;
  const next = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
  requestedBadgeCount = next;
  // 카드 문구 갱신은 배지 적용을 기다릴 이유가 없다(fire-and-forget).
  void import('@tauri-apps/api/core')
    .then(({ invoke }) => invoke('set_taskbar_preview_count', { count: next }))
    .catch(() => {});
  await drainBadgeQueue();
}

/**
 * 마지막 배지 값을 강제로 다시 적용한다.
 *
 * 트레이로 숨겼다 다시 열면 작업표시줄 버튼이 새로 만들어지면서 오버레이가 사라진다.
 * 창 포커스 복귀 시점에 불러 되살린다 — **숫자를 지우는 것이 아니다**(포커스는 '모두 읽음'이 아니다).
 */
export function refreshTaskbarBadge(): void {
  if (!isTauri()) return;
  appliedBadgeCount = null;
  void drainBadgeQueue();
}
